import { cn } from '../../lib/cn.ts';
import { DATA_SLOT_BG } from './PulseChart.tsx';
import type { DataSlot } from './PulseChart.tsx';

export type MeterTone = DataSlot | 'neutral' | 'working' | 'attention' | 'danger';

const FILL: Record<Exclude<MeterTone, DataSlot>, string> = {
  // Soft grey in both themes: light's fg-muted is near-black and would outweigh the names beside it.
  neutral: 'bg-fg-muted in-data-[theme=light]:bg-fg-faint',
  working: 'bg-status-working-fill',
  attention: 'bg-status-attention-fill',
  danger: 'bg-status-danger-fill',
};

export interface MeterBarProps {
  /** 0 to 1. */
  value: number;
  /**
   * Grey by default (a share of finished work is not news). A status tone only when the bar is
   * the status of that work (a failed mission's bar is red); a chart slot for a plain quantity.
   */
  tone?: MeterTone;
  /** Accessible summary: "Progress of Ship the SDK: 60%". */
  label: string;
  className?: string;
}

/**
 * One share as a thin rounded track and its fill: a mission's progress, an epic's roll-up. The
 * fill grows from the left by transform (never width), once when it appears and then as the
 * share changes; under reduced motion it is simply there.
 */
export function MeterBar({ value, tone = 'neutral', label, className }: MeterBarProps) {
  const v = Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
  return (
    <span
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(v * 100)}
      aria-label={label}
      className={cn('relative block h-1.5 min-w-0 overflow-hidden rounded-pill bg-active', className)}
    >
      <span
        aria-hidden
        data-meter-fill=""
        className={cn('motion-segment motion-grow-x absolute inset-0 rounded-pill', typeof tone === 'number' ? DATA_SLOT_BG[tone] : FILL[tone])}
        style={{ scale: `${v} 1` }}
      />
    </span>
  );
}
